<template>
  <div class="margin-top">
    <v-row align="center" class="fill-height ma-0" justify="center">
      <ResHubLogo :height="200" :aspectRatio="1"  />
    </v-row>
    <v-row class="mt-10" justify="center">
      <v-col md="4" sm="12">
        <v-form ref="form" autocomplete="off" lazy-validation @submit.prevent="updatePassword" v-model="isFormValid">
          <v-text-field
            name="currentPassword"
            v-model="currentPassword"
            label="Enter Current Password"
            class="px-4"
            autocapitalize="off" :spellcheck="false"
            :error-messages="currentPwErrors"
            :append-icon="isVisiblePassword ? 'mdi-eye' : 'mdi-eye-off'"
            :type="isVisiblePassword ? 'text' : 'password'"
            @click:append="isVisiblePassword = !isVisiblePassword"
            @input="touchedCurrent = true"
          />

          <p class="body-2 py-3 text-center">
            Create a new password that is at least 6 characters long.
            <span v-if="isPcsUser">This password update does not change your mCare log in.</span>
          </p>

          <v-text-field
            ref="newPw"
            name="newPassword"
            v-model="password"
            label="New Password"
            class="px-4 pt-0"
            autocapitalize="off" :spellcheck="false"
            :error-messages="newPwErrors"
            :append-icon="isVisibleNewPasswords ? 'mdi-eye' : 'mdi-eye-off'"
            :type="isVisibleNewPasswords ? 'text' : 'password'"
            @click:append="isVisibleNewPasswords = !isVisibleNewPasswords"
            @input="touchedNew = true"
          />

          <v-text-field
            ref="repeatPw"
            name="repeatPassword"
            v-model="repeatPassword"
            label="Confirm New Password"
            class="px-4 pt-4"
            autocapitalize="off" 
            :spellcheck="false"
            :error-messages="repeatPwErrors"
            :append-icon="isVisibleNewPasswords ? 'mdi-eye' : 'mdi-eye-off'"
            :type="isVisibleNewPasswords ? 'text' : 'password'"
            @click:append="isVisibleNewPasswords = !isVisibleNewPasswords"
            @input="touchedRepeat = true"
          />

          <div class="px-5">
            <v-btn type="submit" color="primary" block class="mt-10"
                  :disabled="isSubmitDisabled" :loading="isLoading">SAVE</v-btn>
          </div>
        </v-form>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import "firebase/auth"
import * as firebase from "firebase/app";
import {getRegionName} from "@/utils/functionsRegion";
import ResHubLogo from "@/views/login/ResHubLogo.vue";

export default {
  name: 'UpdatePassword',
  components: {
    ResHubLogo,
  },
  data() {
    return {
      currentPassword: null,
      password: null,
      repeatPassword: null,
      isVisiblePassword: false,
      isVisibleNewPasswords: false, 
      isFormValid: false,
      isLoading: false,
      touchedCurrent: false,
      touchedNew: false,
      touchedRepeat: false,
    };
  },
  computed: {
    currentUser() { 
      return this.$store.state.authModule.user 
    },
    currentPwErrors() {
      if (!this.touchedCurrent) return [];
      return !this.currentPassword ? ['Required'] : [];
    },
    newPwErrors() {
      const v = this.password || '';
      if (!this.touchedNew) return [];
      const errs = [];
      if (!v) errs.push('Required');
      if (v.length && v.length < 6) errs.push('Minimum 6 characters');
      if (v && !/[0-9]/.test(v)) errs.push('Must include a number');
      if (v && !/[A-Z]/.test(v)) errs.push('Must include a capital letter');
      if (v && !/[a-z]/.test(v)) errs.push('Must include a lower-case letter');
      if (v && !/[^A-Za-z0-9]/.test(v)) errs.push('Must include a symbol');
      if (v && this.currentPassword && v === this.currentPassword)
        errs.push('New password must be different from current password');
      return errs;
    },
    repeatPwErrors() {
      if (!this.touchedRepeat) return [];
      const errs = [];
      if (!this.repeatPassword) errs.push('Required');
      if (this.repeatPassword && this.repeatPassword.length < 6) errs.push('Minimum 6 characters');
      if (this.password && this.repeatPassword && this.password !== this.repeatPassword)
        errs.push('Password must be same');
      return errs;
    },
    allFilled() {
      return !!this.currentPassword && !!this.password && !!this.repeatPassword;
    },
    passwordsMatch() {
      return this.password === this.repeatPassword;
    },
    newDiffersFromCurrent() {
      return this.password && this.password !== this.currentPassword;
    },
    policyOk() {
      const v = this.password || "";
      return v.length >= 6 && /[0-9]/.test(v) && /[A-Z]/.test(v) && /[a-z]/.test(v) && /[^A-Za-z0-9]/.test(v);
    },
    isSubmitDisabled() {
      return (
        this.isLoading ||
        !this.allFilled ||
        !this.passwordsMatch ||
        !this.newDiffersFromCurrent ||
        !this.policyOk
      );
    },
    isPcsUser() {
      return (this.currentUser.refOrigin == 'rci');
    }
  },
  methods: {
    async isPasswordValid(){
        const credential = firebase.auth.EmailAuthProvider.credential(this.currentUser.email, this.currentPassword)
        const user = firebase.auth().currentUser
        return await user.reauthenticateWithCredential(credential).then(() => {return true}).catch(() => {return false})
    },
    async updatePassword() {

      this.touchedCurrent = this.touchedNew = this.touchedRepeat = true;

      if (!this.allFilled || !this.policyOk || !this.passwordsMatch || !this.newDiffersFromCurrent) {
        this.$toast.open({ message: "Please fix the highlighted fields", type: "error" });
        return;
      }

      this.isFormValid = this.$refs.form.validate();

      if (!this.isFormValid) return;

      this.isLoading = true;

      try {
        const isPasswordValid = await this.isPasswordValid()

        if(isPasswordValid){
           await firebase.app().functions(getRegionName()).httpsCallable('updateUserPassword')({
            staffHubUserId: this.currentUser.id,
            newPassword: this.password
          }).then(async () => {
              await firebase.auth().signInWithEmailAndPassword(this.currentUser.email, this.password)
              await firebase.auth().currentUser.getIdToken(true);
              this.currentPassword = this.password = this.repeatPassword = null;
              this.isLoading = false;
              this.$toast.open({ message: "Password successfully changed", type: "success" });
              this.$router.push({ path: `/settings?tab=user-profile` });
          }).catch((error) => {
            console.error("Unable to set new password", error)
            this.$toast.open({ message: "Unable to set new password", type: "error" });
            this.isLoading = false;
          });
        } else {
          console.error("Current password is not valid")
          this.$toast.open({ message: "Current password is not valid", type: "error"});
          this.isLoading = false;
        }
      } catch (error) {
          console.error(error)
        this.$toast.open({ message: error.message, type: "error"});
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.margin-top{
  margin-top: 40vh;
  transform: translateY(-50%); 
}

@media (max-height: 520px) {
  .margin-top { margin-top: 16px; transform: none; }
}
</style>
